import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { Job, User } from '@core/generated/gql-types';
import useAvailableWorkers from '@/hooks/useAvailableWorkers';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import WorkerSearchbarActions from './WorkerSearchbarActions';
import WorkerSearchbarList from './WorkerSearchbarList';

interface WorkerSearchbarProps {
  job: Job;
  setLoading: (loading: boolean) => void;
  setOpenWorkerSearchModal?: (open: boolean) => void;
  onAssigned?: () => void | Promise<Job | null>;
  showResultsOnFocus?: boolean;
}

const WorkerSearchbar = ({
  job,
  setLoading,
  setOpenWorkerSearchModal,
  onAssigned,
  showResultsOnFocus = false,
}: WorkerSearchbarProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorker, setSelectedWorker] = useState<User | null>(null);
  const [isResultsOpen, setIsResultsOpen] = useState(!showResultsOnFocus);

  const { availableWorkers, isLoading } = useAvailableWorkers({
    job: isResultsOpen ? job : null,
  });

  const { getTerm } = useTemplateStore();

  // Filter workers by search query (full name).
  const handleCancel = () => {
    setSelectedWorker(null);
    setSearchQuery('');
    if (showResultsOnFocus) {
      setIsResultsOpen(false);
      return;
    }
    setOpenWorkerSearchModal?.(false);
  };

  const searchInput = (
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="h-5 w-5 text-gray-400"
        />
      </div>
      <input
        type="text"
        placeholder={`Search matching ${getTerm(TemplateTerm.WORKER, true).toLowerCase()}...`}
        value={searchQuery}
        onFocus={() => setIsResultsOpen(true)}
        onChange={(event) => {
          setSearchQuery(event.target.value);
          setIsResultsOpen(true);
        }}
        className="block w-full rounded-lg border border-blue-300 bg-white py-2 pr-4 pl-10 text-sm placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
      />
    </div>
  );

  const filteredWorkers = useMemo(() => {
    if (!searchQuery.trim()) return availableWorkers;

    return availableWorkers.filter((worker) => {
      const name = `${worker.firstName} ${worker.lastName}`.toLowerCase();
      const email = worker.email?.toLowerCase() || '';
      const query = searchQuery.toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [availableWorkers, searchQuery]);

  const showResults = !showResultsOnFocus || isResultsOpen; // Show results if we are not waiting for the focus or the results are open.

  return (
    <>
      {selectedWorker ? (
        <>
          {/* Render Selected Worker */}

          <div className="space-y-2 rounded-md bg-gray-200 px-3 pt-3 pb-2">
            <div className="flex w-full items-center justify-between gap-2 rounded-md bg-white p-2">
              {/* Selected worker details */}
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center overflow-hidden rounded-full bg-gray-100">
                  {selectedWorker?.profilePictureUrl ? (
                    <img
                      src={selectedWorker.profilePictureUrl}
                      alt={
                        selectedWorker?.firstName +
                        ' ' +
                        selectedWorker?.lastName
                      }
                      className="h-10 w-10 object-contain"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200" />
                  )}
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-gray-900">{`${selectedWorker?.firstName} ${selectedWorker?.lastName}`}</p>
                  <p className="text-sm text-gray-400">
                    {job?.classification?.name || 'Job classification not set'}
                  </p>
                </div>
              </div>
            </div>

            {/* Selected worker actions */}
            <WorkerSearchbarActions
              job={job}
              worker={selectedWorker}
              setLoading={setLoading}
              onCancel={handleCancel}
              onAssigned={onAssigned}
            />
          </div>
        </>
      ) : (
        <div className="space-y-4 rounded-md bg-gray-200 p-6">
          {searchInput}

          {showResults && (
            <>
              <WorkerSearchbarList
                searchQuery={searchQuery}
                job={job}
                isLoading={isLoading}
                filteredWorkers={filteredWorkers}
                setSelectedWorker={setSelectedWorker}
              />

              <WorkerSearchbarActions
                job={job}
                worker={selectedWorker}
                setLoading={setLoading}
                onCancel={handleCancel}
                onAssigned={onAssigned}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default WorkerSearchbar;
